import { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';
import { useAuth } from './AuthContext';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const [cart, setCart] = useState([]);
  const [cartLoading, setCartLoading] = useState(true);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem('cart_session_id') || null);

  useEffect(() => {
    fetchCart();
  }, [user, sessionId]);

  const fetchCart = async () => {
    try {
      const params = {};
      if (!user && sessionId) {
        params.session_id = sessionId;
      }
      const response = await api.get('/cart', { params });
      setCart(response.data.items || []);
    } catch (error) {
      console.error('Failed to fetch cart:', error);
    } finally {
      setCartLoading(false);
    }
  };

  const addToCart = async (productId, quantity = 1) => {
    try {
      const params = {};
      if (!user && sessionId) {
        params.session_id = sessionId;
      }
      const response = await api.post('/cart', { product_id: productId, quantity }, { params });
      
      if (response.data.session_id && !user) {
        setSessionId(response.data.session_id);
        localStorage.setItem('cart_session_id', response.data.session_id);
      }
      
      await fetchCart();
      return response.data;
    } catch (error) {
      console.error('Failed to add to cart:', error);
      throw error;
    }
  };

  const removeFromCart = async (productId) => {
    try {
      const params = {};
      if (!user && sessionId) {
        params.session_id = sessionId;
      }
      await api.delete(`/cart/${productId}`, { params });
      await fetchCart();
    } catch (error) {
      console.error('Failed to remove from cart:', error);
      throw error;
    }
  };

  const updateCartItem = async (productId, quantity) => {
    try {
      const params = { quantity };
      if (!user && sessionId) {
        params.session_id = sessionId;
      }
      await api.put(`/cart/${productId}`, null, { params });
      await fetchCart();
    } catch (error) {
      console.error('Failed to update cart:', error);
      throw error;
    }
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => {
      return total + (item.product?.price || 0) * item.quantity;
    }, 0);
  };

  const getCartCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  const clearCart = async () => {
    try {
      const params = {};
      if (!user && sessionId) {
        params.session_id = sessionId;
      }
      await api.delete('/cart', { params });
      setCart([]);
    } catch (error) {
      console.error('Failed to clear cart:', error);
      throw error;
    }
  };

  return (
    <CartContext.Provider value={{
      cart,
      cartLoading,
      addToCart,
      removeFromCart,
      updateCartItem,
      fetchCart,
      getCartTotal,
      getCartCount,
      clearCart
    }}>
      {children}
    </CartContext.Provider>
  );
};